<template>
  <q-dialog persistent v-model="openDialog">
    <q-card>
      <q-bar class="bg-orange text-white q-pa-sm text-subtitle1">
        <q-icon name="warning" size="sm" />
        <span class="q-ml-sm"> Maximum login limit reached ! </span>
        <q-space />
        <q-btn
          v-close-popup
          text-color="white"
          dense
          size="sm"
          icon="close"
          @click="close()"
        >
          <q-tooltip class="bg-white text-primary">Close</q-tooltip>
        </q-btn>
      </q-bar>
      <q-stepper
        v-model="step"
        ref="stepper"
        color="positive"
        animated
        class="shadow-11"
      >
        <q-step
          :name="1"
          title="Phone Number"
          icon="sms"
          :done="step > 1"
          active-color="positive"
        >
          <q-input
            v-model="v1$.phoneNumber.$model"
            type="tel"
            for="phone"
            filled
            :error="v1$.phoneNumber.$error"
            error-message="Please type your Phone Number"
          />
          <div v-if="countryCode != '+977'" class="row justify-center">
            <div id="recaptcha-container"></div>
          </div>
          <div v-else>
            <verifyOtpNepal @goAhead="goAhead($event)"></verifyOtpNepal>
          </div>
          <div class="text-subtitle1 text-bold q-pa-md text-center">
            Maximum login limit reached. Do you want to continue?
          </div>
          <q-stepper-navigation>
            <q-btn
              @click="sendOtpStep1"
              v-if="countryCode != '+977' && optSteps != 'verifyOtp'"
              color="positive"
              :label="'Continue'"
            />

            <q-btn
              v-if="step > 1"
              flat
              color="primary"
              @click="step = 1"
              label="Back"
              class="q-ml-sm"
            />
          </q-stepper-navigation>
        </q-step>

        <q-step
          :name="2"
          title="Verify OTP"
          icon="sms"
          :done="step > 2"
          active-color="positive"
        >
          <q-input
            filled
            v-model="otpNumber"
            label="OTP Code"
            error-message="Please type your OTP code"
          >
            <template v-slot:prepend>
              <q-icon name="password" />
            </template>

            <template v-slot:append>
              <q-icon
                :name="isOtp ? 'visibility_off' : 'visibility'"
                class="cursor-pointer"
                @click="isOtp = !isOtp"
              />
            </template>

            <template v-slot:after>
              <vue-countdown
                v-if="isCounting"
                :time="180000"
                @end="isCounting = false"
                v-slot="{ totalSeconds }"
              >
                {{ totalSeconds }} s
              </vue-countdown>
            </template>
          </q-input>
          <q-stepper-navigation>
            <q-btn
              @click="verifyOtpStep2"
              color="positive"
              :label="'Continue'"
            />
            <q-btn
              v-if="step > 2"
              flat
              color="primary"
              @click="step = 1"
              label="Back"
              class="q-ml-sm"
            />
          </q-stepper-navigation>
        </q-step>
      </q-stepper>
    </q-card>
  </q-dialog>
</template>

<script>
import { ref, reactive, watch, onMounted, nextTick, onUpdated } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { useRouter, useRoute } from "vue-router";
import VueCountdown from "@chenfengyuan/vue-countdown";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import "intl-tel-input/build/js/utils";
import { useQuasar, LocalStorage } from "quasar";
import useRegister from "src/use/register";
import { getAuth, RecaptchaVerifier } from "firebase/auth";
import firebaseApp from "src/boot/firebase";
import verifyOtpNepal from "./verifyOtpNepal.vue";

export default {
  name: "AuthSignup",
  props: {
    countryCode: {
      type: String,
    },
    iti: {
      type: Object,
    },
    verifyOtp: {
      type: Boolean,
      default: false,
    },
    deviceLimitToken: {
      type: String,
    },
    phoneNumber: {
      type: String,
    },
  },
  components: {
    VueCountdown,
    verifyOtpNepal,
  },
  emits: ["close"],

  setup(props, context) {
    const { multipleDeviceVerifyOtp, sendOptForMultipleDiviceLogin } =
      useRegister();
    const $router = useRouter();
    const step = ref(1);
    const isPwd = ref(true);
    const isOtp = ref(true);
    const isConPwd = ref(true);
    const openDialog = ref(false);
    const $route = useRoute();
    const $q = useQuasar();
    const referrer = ref($route.query?.referrer || null);
    const isNumberEntered = ref(false);
    const recaptchaToken = ref("");
    const countryCode = ref("");
    const isCounting = ref(false);
    const optSteps = ref("phonenumber");
    const close = () => {
      context.emit('close', false);
    };
    const step1Rules = {
      phoneNumber: { required },
    };

    const step2State = reactive({
      otp: "",
    });

    const step2Rules = {
      otp: { required },
    };

    const step1State = reactive({
      phoneNumber: "",
    });

    const v1$ = useVuelidate(step1Rules, step1State, { $lazy: true });
    const otpNumber = ref("");

    const initializeRecaptcha = () => {
      window.recaptchaVerifier = new RecaptchaVerifier(
        "recaptcha-container",
        {
          size: "normal",
          callback: (response) => {
            console.log("reCAPTCHA solved", response);
          },
          "expired-callback": () => {
            console.log("reCAPTCHA expired");
          },
        },
        getAuth(firebaseApp)
      );
    };

    const getPhoneInputElement = () => {
      const input = document.querySelector("#phone");
      console.log(input, "input");
      intlTelInput(input, {
        initialCountry: props.iti.selectedCountryData.iso2,
        customPlaceholder: () => "Phone Number",
      });
      countryCode.value = `+${props.iti.selectedCountryData.dialCode}`;
    };

    watch(
      () => props.verifyOtp,
      (newVal, oldVal) => {
        openDialog.value = newVal;
        if (props.phoneNumber) {
          v1$.value.phoneNumber.$model = props.countryCode + props.phoneNumber;
        }
      }
    );

    onUpdated(() => {
      if (document.querySelector("#recaptcha-container")) {
        nextTick(() => {
          // getPhoneInputElement();
          // Dom is fully updated.
          // Ensure that all changes to the DOM are complete before interacting with it.
          // Skipping nextTick could lead to race conditions, where initializeRecaptcha() runs on an incomplete DOM state.
          if (props.countryCode != "977") initializeRecaptcha();
        });
      }
    });

    const sendOtpStep1 = async () => {
      getPhoneInputElement();
      let token = null;
      optSteps.value = "verifyOtp";
      if (token == null) {
        $q.notify({
          type: "warning",
          message: "Please complete the reCAPTACHA",
        });
      }
      try {
        token = await window.recaptchaVerifier.verify();
        step1State.phoneNumber = props.iti
          .getNumber(intlTelInputUtils.numberFormat.NATIONAL)
          .replaceAll("-", "")
          .replaceAll("(", "")
          .replaceAll(")", "")
          .replaceAll(" ", "");
        recaptchaToken.value = token;
        isCounting.value = true;
        step.value = 2;

        let payload = {
          phone_number: step1State.phoneNumber,
          country_code: countryCode.value,
          recaptcha_token: recaptchaToken.value,
          token: props.deviceLimitToken,
        };
        await sendOptForMultipleDiviceLogin(payload);
        $q.notify({
          type: "positive",
          message: res.data.message,
        });
        LocalStorage.set("validatingOtp", true);
      } catch (error) {
        const { response } = error;
        const { data, status } = response;
        if (status === 400) {
          $q.notify({
            type: "warning",
            message: data.message,
            timeout: 15000,
            textColor: "white",
            actions: [{ icon: "close", color: "white" }],
          });
        } else {
          $q.notify({
            type: "negative",
            message: data.message || "An error occurred. Please try again.",
            timeout: 15000,
            textColor: "white",
            actions: [{ icon: "close", color: "white" }],
          });
        }
      }
    };
    const goAhead = async (token) => {
      optSteps.value = "verifyOtp";
      try {
        isCounting.value = true;
        step.value = 2;

        let payload = {
          phone_number: props.phoneNumber,
          country_code: props.countryCode,
          recaptcha_token: token,
          token: props.deviceLimitToken,
        };

        let res = await sendOptForMultipleDiviceLogin(payload);
        $q.notify({
          type: "positive",
          message: res.data.message,
        });
        LocalStorage.set("validatingOtp", true);
      } catch (error) {
        const { response } = error;
        const { data, status } = response;
        if (status === 400) {
          $q.notify({
            type: "warning",
            message: data.message,
            timeout: 15000,
            textColor: "white",
            actions: [{ icon: "close", color: "white" }],
          });
        } else {
          $q.notify({
            type: "negative",
            message: data.message || "An error occurred. Please try again.",
            timeout: 15000,
            textColor: "white",
            actions: [{ icon: "close", color: "white" }],
          });
        }
      }
    };

    const verifyOtpStep2 = async () => {
      const isMultipleDeviceOTPVerified = await multipleDeviceVerifyOtp(
        props.deviceLimitToken,
        otpNumber.value
      );

      if (isMultipleDeviceOTPVerified) {
        $router.push({ name: "Dashboard" });
      }
    };

    return {
      v1$,
      otpNumber,
      optSteps,
      openDialog,
      step,
      isOtp,
      isPwd,
      isConPwd,
      isCounting,
      isNumberEntered,
      referrer,
      sendOtpStep1,
      verifyOtpStep2,
      goAhead,
      close
    };
  },
};
</script>
